import { Link } from 'react-router-dom';
import { Zap } from 'lucide-react';

const EFFECTIVE_DATE = 'June 7, 2026';
const CONTACT_EMAIL = 'support@dynaparse.com';
const SITE_URL = 'https://dynaparse.com';

function Section({ title, children }) {
  return (
    <div className='mb-8'>
      <h2 className='text-base font-heading font-semibold text-foreground mb-3'>{title}</h2>
      <div className='text-sm text-muted-foreground leading-relaxed space-y-3'>{children}</div>
    </div>
  );
}

export default function PrivacyPage() {
  return (
    <div className='min-h-screen bg-background'>
      {/* Minimal header */}
      <header className='sticky top-0 z-50 bg-card/95 backdrop-blur-md border-b border-border'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between'>
          <Link to='/' className='flex items-center gap-2'>
            <div className='w-8 h-8 rounded-lg bg-primary flex items-center justify-center'>
              <Zap className='w-4 h-4 text-primary-foreground' strokeWidth={2.2} />
            </div>
            <span className='font-heading font-bold text-lg text-foreground'>
              Dyna<span className='text-primary'>Parse</span>
            </span>
          </Link>
          <Link
            to='/'
            className='text-sm text-muted-foreground hover:text-foreground transition-colors'
          >
            ← Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className='max-w-3xl mx-auto px-4 sm:px-6 py-12'>
        <h1 className='text-3xl font-heading font-bold text-foreground mb-1'>Privacy Policy</h1>
        <p className='text-sm text-muted-foreground mb-8'>
          Effective date: {EFFECTIVE_DATE} &nbsp;·&nbsp;{' '}
          <a href={SITE_URL} className='text-primary hover:underline'>
            {SITE_URL}
          </a>
        </p>

        <Section title='1. Introduction'>
          <p>
            DynaParse ("we", "us", or "our") operates the DynaParse document parsing API at <strong className='text-foreground'>{SITE_URL}</strong>. This Privacy Policy describes how we collect, use, and protect your personal information when you use our service.
          </p>
          <p>By using DynaParse, you agree to the collection and use of information in accordance with this policy.</p>
        </Section>

        <Section title='2. Information We Collect'>
          <p><strong className='text-foreground'>Account information:</strong> When you register, we collect your email address. If you sign in with Google, we also receive your Google profile name.</p>
          <p><strong className='text-foreground'>Document content:</strong> Documents you upload for parsing are processed temporarily in memory. <em>We do not permanently store the content of your documents.</em> Extracted field data is returned to you and then discarded.</p>
          <p><strong className='text-foreground'>Usage data:</strong> We record the number of API credits consumed per account. We may log anonymized usage metrics for product improvement.</p>
          <p><strong className='text-foreground'>Payment information:</strong> Payment transactions are handled entirely by Paddle, our third-party payment processor. We do not collect or store your card or bank details.</p>
        </Section>

        <Section title='3. How We Use Your Information'>
          <p>We use the information we collect to:</p>
          <ul className='list-disc pl-5 space-y-1'>
            <li>Provide and maintain the DynaParse document parsing service</li>
            <li>Authenticate your account via email/password or Google OAuth</li>
            <li>Send transactional emails (account verification, credit confirmations)</li>
            <li>Track and display your API credit balance</li>
            <li>Respond to support requests</li>
            <li>Improve and develop the service</li>
          </ul>
          <p>We do not sell, rent, or share your personal data with third parties for marketing purposes.</p>
        </Section>

        <Section title='4. Third-Party Services'>
          <p>We use the following third-party services to operate DynaParse:</p>
          <ul className='list-disc pl-5 space-y-1'>
            <li>
              <strong className='text-foreground'>MongoDB Atlas</strong> — Cloud database for storing account data.{' '}
              <a href='https://www.mongodb.com/legal/privacy-policy' className='text-primary hover:underline' target='_blank' rel='noreferrer'>MongoDB Privacy Policy</a>
            </li>
            <li>
              <strong className='text-foreground'>Google OAuth 2.0</strong> — Optional sign-in method.{' '}
              <a href='https://policies.google.com/privacy' className='text-primary hover:underline' target='_blank' rel='noreferrer'>Google Privacy Policy</a>
            </li>
            <li>
              <strong className='text-foreground'>Resend</strong> — Transactional email delivery via noreply@dynaparse.com.{' '}
              <a href='https://resend.com/privacy' className='text-primary hover:underline' target='_blank' rel='noreferrer'>Resend Privacy Policy</a>
            </li>
            <li>
              <strong className='text-foreground'>Paddle</strong> — Payment gateway for credit purchases.{' '}
              <a href='https://www.paddle.com/legal/privacy' className='text-primary hover:underline' target='_blank' rel='noreferrer'>Paddle Privacy Policy</a>
            </li>
          </ul>
          <p>These services have their own privacy policies; we encourage you to review them.</p>
        </Section>

        <Section title='5. Data Retention'>
          <p>We retain your account information (email, credit balance) for as long as your account is active. Document content uploaded for parsing is discarded immediately after extraction is complete.</p>
          <p>You may request account deletion at any time by emailing <a href={`mailto:${CONTACT_EMAIL}`} className='text-primary hover:underline'>{CONTACT_EMAIL}</a>. We will delete your data within 14 business days.</p>
        </Section>

        <Section title='6. Security'>
          <p>We implement industry-standard security measures including HTTPS encryption in transit, hashed passwords (bcrypt), and JWT-based authentication tokens. While we strive to protect your data, no method of transmission over the internet is 100% secure.</p>
        </Section>

        <Section title='7. Your Rights'>
          <p>Depending on your jurisdiction, you may have the following rights regarding your personal data:</p>
          <ul className='list-disc pl-5 space-y-1'>
            <li><strong className='text-foreground'>Access:</strong> Request a copy of the data we hold about you.</li>
            <li><strong className='text-foreground'>Correction:</strong> Request correction of inaccurate data.</li>
            <li><strong className='text-foreground'>Deletion:</strong> Request deletion of your account and associated data.</li>
            <li><strong className='text-foreground'>Portability:</strong> Request your data in a portable format.</li>
          </ul>
          <p>To exercise these rights, contact us at <a href={`mailto:${CONTACT_EMAIL}`} className='text-primary hover:underline'>{CONTACT_EMAIL}</a>.</p>
        </Section>

        <Section title='8. Children&apos;s Privacy'>
          <p>DynaParse is not directed at individuals under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected such information, please contact us and we will delete it promptly.</p>
        </Section>

        <Section title='9. Changes to This Policy'>
          <p>We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on this page and updating the effective date.</p>
        </Section>

        <Section title='10. Contact'>
          <p>
            If you have questions about this Privacy Policy, contact us at:{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className='text-primary hover:underline font-medium'>
              {CONTACT_EMAIL}
            </a>
          </p>
          <p className='text-foreground/70'>DynaParse &nbsp;·&nbsp; <a href='https://dynaparse.com' className='text-primary hover:underline'>dynaparse.com</a></p>
        </Section>

        <div className='border-t border-border pt-6 mt-4 flex gap-4 text-sm'>
          <Link to='/terms' className='text-primary hover:underline'>Terms of Service</Link>
          <Link to='/' className='text-muted-foreground hover:text-foreground'>← Back to Home</Link>
        </div>
      </main>
    </div>
  );
}
